import pubsub from "@viewlift/pubsub";
import PackageJson from '../../package.json';
import playerPackageJson from '@viewlift/player/package.json';
import { getStorageItem } from "../helpers/storageManager";
import { VISITOR } from "./Configuration";
const playerVersion = playerPackageJson.version
const version = PackageJson.version
var Media
let loadMap = new Map()
let adPayload = null
let adBeforeSessionStarted = false
export let VideoAnalyticsProviderWrapper
export const isAnalyticsSessionRunning = (function () {
  let isSessionRunning = false;
  return {
    get: () => isSessionRunning,
    set: (value) => {
      isSessionRunning = value;
    },
    getVideoId: () => loadMap.entries().next().value?.[0]
  };
})();


function createPlayerAdapter(player, entitlementData = {}) {
  if (!player) return null

  const declaredType = entitlementData?.player?.type?.toLowerCase?.()
  const isBitmovinDecl = declaredType === "bitmovin"
  const isVideojsDecl = declaredType === "videojs"
  // sanity fallback incase no type is defined
  const looksLikeBitmovin = typeof player.getCurrentTime === "function" || typeof player.getDuration === "function" || isBitmovinDecl
  const looksLikeVideojs = typeof player.currentTime === "function" || typeof player.duration === "function" || isVideojsDecl

  const mode = isBitmovinDecl ? "bitmovin" : isVideojsDecl ? "videojs" : (looksLikeBitmovin ? "bitmovin" : (looksLikeVideojs ? "videojs" : "unknown"))
  const isLiveStream = entitlementData?.streamingInfo?.isLiveStream || false
  const mapEvents = (evt) => {
    const eventMap = {
      loadeddata: mode === "bitmovin" ? "loadedmetadata" : "loadeddata",
      play: "play",
      pause: mode === "bitmovin" ? "paused" : "pause",
      seeking: mode === "bitmovin" ? isLiveStream ? "timeshift" : "seek" : "seeking",
      seeked:  mode === "bitmovin" ? isLiveStream ? "timeshifted" : "seeked" : "seeked",
      waiting: mode === "bitmovin" ? "stallstarted" : "waiting",
      timeupdate: mode === "bitmovin" ? "timechanged" : "timeupdate",
      ended: mode === "bitmovin" ? "playbackfinished" : "ended",
      chromecastConnected: "chromecastConnected",
      chromecastDisconnected: "chromecastDisconnected"
    }
    return eventMap[evt] || evt
  }

  function on(event, handler) {
    const mapped = mapEvents(event)
    if (mode === "bitmovin") {
      try { player.on(mapped, handler) } catch (e) {
        if (player.EVENT && player.EVENT[mapped?.toUpperCase?.()]) {
          player.on(player.EVENT[mapped.toUpperCase()], handler)
        } else {
          console.warn("bitmovin.on fallback for", mapped, e)
        }
      }
    } else {
      if (typeof player.on === "function") {
        player.on(mapped, handler)
      } else {
        console.warn("Player has no .on for", mapped)
      }
    }
  }

  function off(event, handler) {
    const mapped = mapEvents(event)
    if (mode === "bitmovin") {
      try { player.off(mapped, handler) } catch (e) {
        if (player.EVENT && player.EVENT[mapped?.toUpperCase?.()]) {
          player.off(player.EVENT[mapped.toUpperCase()], handler)
        }
      }
    } else {
      if (typeof player.off === "function") {
        player.off(mapped, handler)
      } else {
        console.warn("Player has no .off for", mapped)
      }
    }
  }

  function currentTime() {
    if (mode === "bitmovin") {
      try {
        return typeof player.getCurrentTime === "function" ? player.getCurrentTime() : (player.currentTime ? player.currentTime() : 0)
      } catch (e) { return 0 }
    } else {
      return typeof player.currentTime === "function" ? player.currentTime() : (player.currentTime || 0)
    }
  }

  function duration() {
    if (mode === "bitmovin") {
      try {
        return typeof player.getDuration === "function" ? player.getDuration() : (player.duration ? player.duration() : 0)
      } catch (e) { return 0 }
    } else {
      return typeof player.duration === "function" ? player.duration() : (player.duration || 0)
    }
  }

  function play() {
    try {
    if (mode === "bitmovin") {
      if (typeof player?.play === "function") {
        return player.play();
      }
      return; // if play exists but isn't a function
    }

    // Default case (video js)
    if (typeof player?.play === "function") {
      return player.play();
    }
    return
  } catch (e) {
    console.log("Error while calling play():", e)
    return
  }
  }

  function ready(cb) {
    if (mode === "bitmovin") {
      if (typeof player.isReady === "function" && player.isReady()) {
        cb()
      } else if (player.on) {
        const readyHandler = () => { 
          off('ready', readyHandler);
          off('loadeddata', readyHandler)
          cb() 
        }
        on('ready', readyHandler)
        on('loadeddata', readyHandler)
      } else {
        cb()
      }
    } else {
      if (typeof player.ready === "function") {
        player.ready(cb)
      } else if (typeof player.on === "function") {
        const readyHandler = () => { off('ready', readyHandler); cb() }
        on('ready', readyHandler)
      } else {
        cb()
      }
    }
  }

  function isFullscreen() {
    try {
        if (typeof player.isFullscreen === "function") return player.isFullscreen()
        if (player.isFullscreen !== undefined) return !!player.isFullscreen
    } catch (e) { return false }
    return false
  }

  function currentSrc() {
    try {
      if (mode === "bitmovin") {
        if (typeof player.getSource === "function") {
          const s = player.getSource()
          return (s && s.src) || ""
        }
      } else {
        // videojs: player.currentSrc()
        if (typeof player.currentSrc === "function") return player.currentSrc()
        if (player.currentSrc) return player.currentSrc
      }
    } catch (e) {}
    return ""
  }

  function any(eventsArray = [], handler) {
    const handlers = []
    const cleanup = () => {
      eventsArray.forEach((evt, idx) => {
        off(evt, handlers[idx])
      })
    }
    eventsArray.forEach((evt, idx) => {
      handlers[idx] = function () {
        cleanup()
        handler()
      }
      on(evt, handlers[idx])
    })
  }

  return {
    raw: player,
    mode,
    on,
    off,
    currentTime,
    duration,
    play,
    ready,
    isFullscreen,
    currentSrc,
    any
  }
}
(function () {

  function VideoAnalyticsProvider(player, mediaInstance,entitlementData) {
    Media = mediaInstance
    if (!player && !Media) {
      throw new Error("Player reference cannot be null.")
    }
    // create adapter
    this._adapter = createPlayerAdapter(player, entitlementData)
    if (!this._adapter) {
      throw new Error("Unsupported player instance.")
    }

    //Constructor
    this._player = this._adapter
    this._adComplete = false
    this._entitlementData = entitlementData
    this._stopPlayheadUpdate = false
    this._mediaTracker = Media?.getInstance();
    this._isSeekComplete = true;
    this._isChapterStart = false
    this._isBufferComplete = true
    this.isWaiting = false
    this._isSessionStarted = false
    this.lastUpdateTime = null
    this.isAutoPlayed = false
    this.videotmsid = null
    this.programmeDuration = 86400
    this.lcProgrammeDetails = null
    this.programmeStartTime = 0
    this.lcProgrammeName = null
    this.preAdRollPlayed = false
    this.lcSubTitle = null
    this._breakStartPlayhead = 0
    this._playheadInterval = null
    this._chapterStartInterval = null
    this._chapterIndex = 0
    this._isSeries = entitlementData?.mediaType === "EPISODIC" || false
    this._isLiveStream = entitlementData?.streamingInfo?.isLiveStream || false
    this.isLinearStream = this._isLiveStream && entitlementData?.contentType === "LINEARCHANNEL"

    this._handlers = {
      loadeddata: this._handleLoadedData.bind(this),
      play: this._onPlay.bind(this),
      pause: this._onPause.bind(this),
      seeking: this._onSeekStart.bind(this),
      seeked: this._onSeekComplete.bind(this),
      waiting: this._onBufferStart.bind(this),
      timeupdate: this._onUpdateTrigger.bind(this),
      ended: this._onComplete.bind(this),
      chromecastConnected: () => {
        console.log("chromecast connected")
        window.isCastDevice = true
      },
      chromecastDisconnected: () => {
        console.log("chromecast disconnected")
        window.isCastDevice = false
      },
    };
    this._pubsubHandlers = {
      adEventHandler: this._handleAdEvent.bind(this),
      startNextSession: () => { this._onLoad() },
      updatePlayerData: (data) => { this._entitlementData = data },
      videoEnds: () => { this._onComplete() },
      destroyAnalytics: () => { this?.destroy() }
    }
    if (!this?._mediaTracker) return
    this._installEventListeners();
  }

  function secondsWithinDay(streamSeconds) {
    if (!isFinite(streamSeconds)) return 0;
    const DAY = 86400;
    const rem = ((streamSeconds % DAY) + DAY) % DAY;
    return rem;
  }

  VideoAnalyticsProvider.prototype._handleLoadedData = function () {
    this.isAutoPlayed = false
    this._checkForAutoPlay(this._player)
  }

  VideoAnalyticsProvider.prototype._handleAdEvent = function (data) {
    if(!data?.adsData?.ad) return
    switch (data?.adsData?.eventType) {
      case "breakStart":
        this._onBreakStart(data)
        break;
      case "firstQuartile":
        this._onAdStart(data)
        break;
      case "complete":
        this._onAdComplete()
        break;
      case "breakEnd":
        this?._onBreakComplete()
        break;
      default:
        //
    }
  }

  function isPreRollAd() {
    let adsQueueList = window?.adsQueueList || []
    if(adsQueueList.length && adsQueueList[0]?.startTimeInSeconds === 0 && adsQueueList[0]?.ads.length) {
      return true
    } else {
      return false
    }
  }

  function getProgrammeElapsedSeconds(lcProgrammeDetails) {
    let startTime = window?.lcProgrammeDetails?.programmeStartTime || lcProgrammeDetails || "";
    const startDate = new Date(startTime);
    let now = Date.now();
    return Math.floor((now - startDate.getTime()) / 1000);
  }

  function calculateChapterLength(chapterIndex, entitlementData) {
    let adsQueueList = window?.adsQueueList || []
    const isLiveStream = entitlementData?.streamingInfo?.isLiveStream || false
    const isLiveGame = entitlementData?.isGame && isLiveStream || false
    if(isLiveGame) {
      return entitlementData?.scheduleGameRuntime || 0
    }
    if(!adsQueueList?.length) return entitlementData?.runtime || window?.lcProgrammeDetails?.programDuration || 0
    let previousChapter = adsQueueList[chapterIndex - 1]
    let startingPoint = previousChapter?.startTimeInSeconds + previousChapter?.durationInSeconds
    let chapteroffset =  adsQueueList[chapterIndex]?.startTimeInSeconds
    return chapteroffset ? (chapteroffset - startingPoint) : 0
  }
  function getContentTime(currentTime) {
    const adPods = window?.adsQueueList || [];
    if (!Array.isArray(adPods) || !adPods.length) return currentTime;

    let adTime = 0;

    for (const pod of adPods) {
      const start = pod.startTimeInSeconds;
      const end = start + (pod.durationInSeconds || 0);

      if (currentTime >= end) {
        adTime += pod.durationInSeconds || 0;
      }
    }

    return currentTime - adTime;
  }

  VideoAnalyticsProvider.prototype.destroy = function (isComplete = false) {
    if (this._mediaTracker) {
      if(!isComplete) this._onUnload(); 
      this._mediaTracker = null;
    }

    if (this._player) {
      this._player = null;
      this._uninstallEventListeners();
    }
  };

  VideoAnalyticsProvider.prototype._onLoad = function (payload) {
    if(!this?._mediaTracker) return
    if (this.preAdRollPlayed) return
    this.lcProgrammeDetails = window?.lcProgrammeDetails || this._entitlementData?.lcProgrammeDetails
    var videoInfo = this._entitlementData
    const isLiveStream = videoInfo?.streamingInfo?.isLiveStream || false
    const isLiveGame = videoInfo?.isGame && isLiveStream || false
    let previousPath = sessionStorage.getItem("adobe-analytics-video-path")
    if(previousPath && previousPath === window?.location?.pathname && !this._entitlementData?.replay) {
      loadMap.clear()
    }
    sessionStorage.setItem("adobe-analytics-video-path", window?.location?.pathname)
    if(!this.isLinearStream) {
      if(loadMap.has(this._entitlementData?.gist?.id) && !this._entitlementData?.replay) return 
      else {
        loadMap.clear()
        loadMap.set(this._entitlementData?.gist?.id,true)
      }
    }else {
      if(loadMap.has(this.lcProgrammeDetails?.videotmsid)) return
      else {
        loadMap.clear()
        loadMap.set(this.lcProgrammeDetails?.videotmsid,true)
        this.videotmsid = loadMap.entries().next().value[0]
        this.lcProgrammeName = this.lcProgrammeDetails?.lcProgrammeName
        this.lcSubTitle = this.lcProgrammeDetails?.lcSubTitle
        this.programmeDuration = this.lcProgrammeDetails?.programDuration
      }
    }
    let streamType = "fullEpisode"
    if(!this._isSeries) {
      streamType = "movie"
    }
    if(this?._entitlementData?.isSleReplay) {
      streamType = "fullEventReplay"
    }
    if(this.isLinearStream) { 
      streamType = "Linear"
      this.programmeStartTime = getProgrammeElapsedSeconds(this.lcProgrammeDetails)
    }
    let runtime = this.isLinearStream ? this.programmeDuration : isLiveStream ? 86400 : videoInfo.runtime
    if(isLiveGame) {
      runtime = videoInfo?.scheduleGameRuntime
      streamType = "sle"
    }
    var mediaInfo = Media.createMediaObject(!this.isLinearStream ? videoInfo.gist.title : this.lcSubTitle || this.lcProgrammeName,!this.isLinearStream ? videoInfo.gist.id : this.videotmsid, runtime, streamType, Media.MediaType.Video);
    mediaInfo[Media.MediaObjectKey.GranularAdTracking] = true;
    if(this._entitlementData?.watchedTimeHistory > 10) {
      mediaInfo[Media.MediaObjectKey.MediaResumed] = true;
    }
    let data = this._formulatePayload({type: "VIDEO_LOAD", info : {}})
    data[Media.VideoMetadataKeys.Show] = this.lcProgrammeName || this._entitlementData?.series?.title || videoInfo.gist.title
    data[Media.VideoMetadataKeys.Episode] = !this.isLinearStream ? !this._isSeries ? "N/A" : videoInfo.gist.title : this.lcSubTitle || "N/A"
    if(this._entitlementData?.replay && this._isSessionStarted) return
    if(this._isSessionStarted) {
      this._onUnload()
    }
    this._mediaTracker.trackSessionStart(mediaInfo,data);
    this._isSessionStarted = true
    isAnalyticsSessionRunning.set(true)
    this._onPlayheadUpdate()
    this._onPlay()
    if(adBeforeSessionStarted) {
      console.log("ad already started before session started", adPayload)
      this._onBreakStart(adPayload)
    }
    if (!this?._isChapterStart) {
      this._chapterStartInterval = setInterval(() => {
          if(window?.adsQueueList?.length) {
          if(!isPreRollAd()) {
            clearInterval(this._chapterStartInterval)
              this._onChapterStart()
              this._isChapterStart = true
          }
        }else {
          setTimeout(() => {
            clearInterval(this._chapterStartInterval)
            if(!this?._isChapterStart) {
            this._onChapterStart()
            this._isChapterStart = true
            }
          }, 1000)
        }
        }, 1000)
    }
  };

  VideoAnalyticsProvider.prototype._onUnload = function () {
    if(!this?._mediaTracker) return
    this._chapterIndex = 0
    this._isSessionStarted = false
    isAnalyticsSessionRunning.set(false)
    this._isChapterStart = false
    this.preAdRollPlayed = false
    this.programmeStartTime = 0
    adBeforeSessionStarted = false
    this._mediaTracker.trackSessionEnd();
    // loadMap.clear()
  };
  let onPlayDebounce = null
  VideoAnalyticsProvider.prototype._onPlay = function () {
    if (!this?._mediaTracker) return
    if (!this._isSessionStarted) return
    if(this._isLiveStream) {
      this.programmeStartTime = getProgrammeElapsedSeconds(this.lcProgrammeDetails)
    }
    clearTimeout(onPlayDebounce)
    onPlayDebounce = setTimeout(() => {
      if(!this._isBufferComplete) {
        this._isBufferComplete = true
      }
      this._mediaTracker.trackPlay();
    }, 0)
  }
  VideoAnalyticsProvider.prototype._onPause = function () {
    if(!this?._mediaTracker) return
    if(!this._isSessionStarted) return
    if(!this._isChapterStart) {
      clearInterval(this._playheadInterval)
    }
     if(!this._isBufferComplete) {
       this._mediaTracker.trackPlay();
      }
    this._mediaTracker.trackPause();
  };

  VideoAnalyticsProvider.prototype._onSeekStart = function () {
    if(!this?._mediaTracker) return
    if(!this._isSessionStarted) return
    this._isSeekComplete = false
    this._mediaTracker.trackEvent(Media.Event.SeekStart, null, null);
  };

  VideoAnalyticsProvider.prototype._onSeekComplete = function () {
    if(!this?._mediaTracker) return
    if(!this._isSessionStarted) return
    if(this._isSeekComplete) return
    this._mediaTracker.trackEvent(Media.Event.SeekComplete, null, null);
    this._isSeekComplete = true
  };

  VideoAnalyticsProvider.prototype._onBufferStart = function () {
    if(!this?._mediaTracker) return
    if(!this._isSessionStarted) return
    // buffer needs to be called after seek
    if(!this._isSeekComplete) {
      this._onSeekComplete()
    }
      this.Waiting()
      this._isBufferComplete = false
      this._mediaTracker.trackEvent(Media.Event.BufferStart, null, null);
  };

  VideoAnalyticsProvider.prototype._onBreakStart = function (data) {
    adPayload = data
    if(!this._isSessionStarted && !adBeforeSessionStarted) {
      adBeforeSessionStarted = true
      console.log("adBeforeSessionStarted",adBeforeSessionStarted)
    }
    if(!this?._mediaTracker) return
    adBeforeSessionStarted = false
    if(this?._isChapterStart) {
      this._onChapterComplete()
      this._isChapterStart = false
    }
    if(!this._isSessionStarted && !this.preAdRollPlayed) {
      this._onLoad()
      this.preAdRollPlayed = true
    }
    if(!this?._isChapterStart) {
      this._stopPlayheadUpdate = true
    }
    const { startTimeInSeconds: rawStartTime, ad, adIndex } = data?.adsData;
    const startTimeInSeconds = Math.round(rawStartTime)
    const position = adIndex + 1 || 1
    
    var _adBreakInfo = ad
    if(!_adBreakInfo) return
    var adBreakInfo = Media.createAdBreakObject(_adBreakInfo?.adTitle, position, startTimeInSeconds);
  
      var _adInfo = _adBreakInfo
      var resolver = _adInfo?.adSystem
      var creativeId = _adInfo?.creativeId
      let adContextData = this._formulatePayload({type: "AD", info : _adInfo})
      adContextData["resolver"] = resolver || "N/A"
      adContextData["creative"] = creativeId || "N/A"
      adContextData["advertiser"] = "N/A"
      adContextData["creativeName"] = "N/A"

    this._mediaTracker.trackEvent(Media.Event.AdBreakStart, adBreakInfo);
    adPayload = null
  };

  VideoAnalyticsProvider.prototype._onAdStart = function (data) {
    if(!this?._mediaTracker) return
    if(this.preAdRollPlayed) {
      this.preAdRollPlayed = false
    }
    this._adComplete = false
    const { startTimeInSeconds: rawStartTime, ad, adIndex } = data?.adsData;
    const startTimeInSeconds = Math.round(rawStartTime);
    
    var _adBreakInfo = ad
    if(!_adBreakInfo) return
      // var adBreakInfo = Media.createAdBreakObject(_adBreakInfo?.adTitle, startTimeInSeconds, startTimeInSeconds);
  
      var _adInfo = _adBreakInfo
      var resolver = _adInfo?.adSystem
      var creativeId = _adInfo?.creativeId
      var adInfo = Media.createAdObject(_adInfo?.adTitle, _adInfo?.adId, startTimeInSeconds,_adInfo?.durationInSeconds);
      let adContextData = this._formulatePayload({type: "AD", info : _adInfo})
      adContextData["resolver"] = resolver || "N/A"
      adContextData["creative"] = creativeId || "N/A"
      adContextData["advertiser"] = "N/A"
      adContextData["creativeName"] = "N/A"

    // this._mediaTracker.trackEvent(Media.Event.AdBreakStart, adBreakInfo);
    this._mediaTracker.trackEvent(Media.Event.AdStart, adInfo, adContextData);
  };

  VideoAnalyticsProvider.prototype._onAdComplete = function () {
    if(!this?._mediaTracker) return
    if(!this._adComplete) {
      this._mediaTracker.trackEvent(Media.Event.AdComplete);
      this._adComplete = true
    }
  };

  VideoAnalyticsProvider.prototype._onBreakComplete = function() {
    if(!this?._mediaTracker) return
    this._stopPlayheadUpdate = false
    this._onAdComplete()
    this._mediaTracker.trackEvent(Media.Event.AdBreakComplete);
    clearInterval(this._playheadInterval)
    this._breakStartPlayhead = 0
    if(!this?._isChapterStart) {
      this._onChapterStart()
      this._isChapterStart = true
    }
  }

  VideoAnalyticsProvider.prototype._onChapterStart = function () {
    if(!this?._mediaTracker) return
     let currentTime = 0
    if(this?._player) {
      currentTime = this?._player?.currentTime() || 0
    }
    if(this?._isLiveStream) {
      currentTime = 0
    }
    this._chapterIndex += 1
    let chapterTitle = this._entitlementData?.gist?.title + ` - Chapter ${this._chapterIndex}`; 
    let chapterLength = calculateChapterLength(this._chapterIndex, this._entitlementData)
    var chapterInfo = Media.createChapterObject(chapterTitle, this._chapterIndex, chapterLength, currentTime);
    let chapterContextData = this._formulatePayload({type: "CHAPTER", info : chapterInfo})

    this._mediaTracker.trackEvent(Media.Event.ChapterStart, chapterInfo, chapterContextData);
  };

  VideoAnalyticsProvider.prototype._onChapterComplete = function () {
    if(!this?._mediaTracker) return
    this._mediaTracker.trackEvent(Media.Event.ChapterComplete);
  };

  VideoAnalyticsProvider.prototype._onComplete = function () {
    if(!this?._mediaTracker) return
    if (this?._isChapterStart) {
      this._onChapterComplete()
      this._isChapterStart = false
    }
    this._chapterIndex = 0
    this._mediaTracker.trackComplete();
    this.programmeStartTime = 0
    this._isSessionStarted = false
    isAnalyticsSessionRunning.set(false)
    if(!this._isLiveStream) {
      this.destroy(true)
    }
  };

  VideoAnalyticsProvider.prototype._onPlayheadUpdate = function () {
    if (!this?._mediaTracker) return
    if(!this._isSessionStarted) return
    if(this.isLinearStream) {
      this.programmeStartTime = getProgrammeElapsedSeconds(this.lcProgrammeDetails)
      this.programmeStartTime = this.programmeStartTime + 1
      this._mediaTracker.updatePlayhead(this.programmeStartTime);
      return
    }
    if(this._isLiveStream) {
      let currentTime = secondsWithinDay(this._player.currentTime())
      this._mediaTracker.updatePlayhead(currentTime);
      return
    }
    if (!this._stopPlayheadUpdate) {
      const calculatedTime = getContentTime(this._player.currentTime());
      this._mediaTracker.updatePlayhead(calculatedTime);
      this._breakStartPlayhead = calculatedTime
    }else {
      this._mediaTracker.updatePlayhead(this._breakStartPlayhead);
    }
  }
  VideoAnalyticsProvider.prototype._onUpdateTrigger = function () {
    const now = Date.now();
    if(!this.lastUpdateTime) this.lastUpdateTime = now
    if (this.lastUpdateTime && (now - this.lastUpdateTime < 1000)) {
      return;
    }
    this.lastUpdateTime = now;

    let isVideoChanged = false
    if(this?.isLinearStream) {
      isAnalyticsSessionRunning.getVideoId() !== window?.lcProgrammeDetails?.videotmsid ? isVideoChanged = true : isVideoChanged = false
    }
    if ((!this.isAutoPlayed && !isAnalyticsSessionRunning.get()) || isVideoChanged) {
      this.isAutoPlayed = true;
      this._onLoad();
    }

    const current = this._player?.currentTime();
    const total = this._player?.duration();
    if (!this._isLiveStream && total - current < 1) {
      this._onComplete()
    }

    this._onPlayheadUpdate();
  }

  VideoAnalyticsProvider.prototype._checkForAutoPlay = function (player) {
    const self = this
     player.ready(() => {
      const playPromise = player.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (!self.isAutoPlayed && !isAnalyticsSessionRunning.get()) {
              self.isAutoPlayed = true
              self._onLoad()
            }
          })
          .catch((err) => {
            self.isAutoPlayed = false
          })
      }
    });
  }

  VideoAnalyticsProvider.prototype.Waiting = function () {
    if(this.isWaiting) return;

    const bufferStop = () => {
      this.isWaiting = false;
      this._onPlay()
    };
    this.isWaiting = true;
    this._player.any(['playing', 'timeupdate'], bufferStop);
  }

  VideoAnalyticsProvider.prototype._installEventListeners = function () {
    const player = this._player
    player?.on("loadeddata", this._handlers.loadeddata)
    player?.on("play", this._handlers.play)
    player?.on("pause", this._handlers.pause)
    player?.on("seeking", this._handlers.seeking)
    player?.on("seeked", this._handlers.seeked)
    player?.on("waiting", this._handlers.waiting)
    player?.on("timeupdate", this._handlers.timeupdate)
    player?.on("ended", this._handlers.ended)

    if (window?.cast?.framework) {
      player?.on('chromecastConnected', this._handlers.chromecastConnected)
      player?.on('chromecastDisconnected', this._handlers.chromecastDisconnected)
    }
    pubsub.subscribe("ad-event-handler", this._pubsubHandlers.adEventHandler)
    pubsub.subscribe("start-next-session", this._pubsubHandlers.startNextSession)
    pubsub.subscribe("update-player-data", this._pubsubHandlers.updatePlayerData)
    pubsub.subscribe("video-ends", this._pubsubHandlers.videoEnds);
    pubsub.subscribe("destroy-adobe-analytics-instance", this._pubsubHandlers.destroyAnalytics);
  };

  VideoAnalyticsProvider.prototype._uninstallEventListeners = function () {
    const player = this._player
    player?.off("loadeddata", this._handlers.loadeddata)
    player?.off("play", this._handlers.play)
    player?.off("pause", this._handlers.pause)
    player?.off("seeking", this._handlers.seeking)
    player?.off("seeked", this._handlers.seeked)
    player?.off("waiting", this._handlers.waiting)
    player?.off("timeupdate", this._handlers.timeupdate)
    player?.off("ended", this._handlers.ended)

    if (window?.cast?.framework) {
      player?.off('chromecastConnected', this._handlers.chromecastConnected)
      player?.off('chromecastDisconnected', this._handlers.chromecastDisconnected)
    }
    
    pubsub.unsubscribe("ad-event-handler", this._pubsubHandlers.adEventHandler)
    pubsub.unsubscribe("start-next-session", this._pubsubHandlers.startNextSession)
    pubsub.unsubscribe("update-player-data", this._pubsubHandlers.updatePlayerData)
    pubsub.unsubscribe("video-ends", this._pubsubHandlers.videoEnds)
    pubsub.unsubscribe("destroy-adobe-analytics-instance", this._pubsubHandlers.destroyAnalytics)
  };

  function getTimeSegment(date) {
    const hour = date.getHours();

    if (hour >= 5 && hour < 12) {
      return "morning";
    } else if (hour >= 12 && hour < 17) {
      return "afternoon";
    } else if (hour >= 17 && hour < 22) {
      return "primetime";
    } else {
      return "latenight";
    }
  }

  function getStreamType(url) {
    if (!url || typeof url !== "string") return null;

    const lowerUrl = url.toLowerCase();

    if (lowerUrl.includes(".mpd")) {
      return "DASH";
    } else if (lowerUrl.includes(".m3u8")) {
      return "HLS";
    } else {
      return "MP4";
    }
  }

  function getFormattedData(ms) {
    const now = ms ? new Date(ms * 1000) : new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const min = now.getMinutes().toString().padStart(2, '0');
    const hrs = now.getHours().toString().padStart(2, '0');
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const day = dayNames[now.getDay()]
    return {date : `${month}/${dd}/${year}`, 
            day, 
            hrs, 
            min, 
            year,
            now};
  }
  VideoAnalyticsProvider.prototype._formulatePayload = function () {
   
    const seriesTitle = this._entitlementData?.mediaType === "EPISODIC" ? this._entitlementData?.series?.title : null
    const isFullscreen = this._player?.isFullscreen() ? " FullScreen" : "Normal";
    const isLiveStream = this._entitlementData?.streamingInfo?.isLiveStream || false
    const {date,hrs,day,min,now } = getFormattedData()
    const programmingType = this._entitlementData?.metadata?.filter((keys) => keys?.name === "programmingType")?.map((program) => program?.value)
    let videolanguage = this._entitlementData?.gist?.languageCode
    const defaultLanguage = window?.app_data?.appcmsMain?.languages?.default?.code
    if(videolanguage === "default") {
      videolanguage = defaultLanguage
    }
    let deviceType = sessionStorage.getItem("deviceType")
    let monetizationModels = this._entitlementData?.monetizationModels?.map((plan) => plan?.type) || []
    var contextData = {
      "appVersion" : version,
      "videoapp": "N/A", // only applicable for mobile apps
      "playerVersion": playerVersion,
      "videoplatform" : deviceType,
      "videoguid" : this._entitlementData?.gist?.id,
      "videoprogram" : seriesTitle || this._entitlementData?.gist?.title,
      "videotitle" : this._entitlementData?.gist?.title,
      "videoinitiate" : window?.videoinitiate || "Manual",
      "videodate" : date,
      "videoday" : day,
      "videohour" : `${hrs}:00`,
      "videominute" : min,
      "videoairdate" : getFormattedData(this._entitlementData?.publishDate).date,
      "videostatus" :  monetizationModels.includes("FREE") ? "Unrestricted" : "Restricted",
      "videoplayerurl" : window?.location?.href || "",
      "videodomain" : window?.location?.origin || "",
      "videocallsign" : "N/A",
      "videocliptype" : "N/A",
      "videoscreen" : isFullscreen,
      "videorequestorid": window?.app_data?.appcmsPlatform?.tveSettings?.requestorID,
      "videolanguage" : videolanguage || "N/A",
      "videodaypart" : getTimeSegment(now),
      "videoplayertech" : getStreamType(this._player.currentSrc()),
      "videoresearchtitle" : this._entitlementData?.videoresearchtitle || this._entitlementData?.gist?.title,
      "ovp" : deviceType,
      "videobroadcast" : isLiveStream ? "Broadcast" : "Digital",
      "videocrossdevice" : window?.isCastDevice ? "T" : "F",
      "videosport" : this._entitlementData?.sportsName || "N/A",
      "videoleague" : this._entitlementData?.leagueName || "N/A",
      "videocliptype" : programmingType?.length ? programmingType[0] : "N/A",
      "videocastsource" : "N/A",
      "videosponsor" : "N/A",
      "publisher" : VISITOR.MARKETING_CLOUD_ORG_ID, //need clarrification,
    }
    if(this._entitlementData?.network || this._entitlementData?.seriesNetwork || this._entitlementData?.videoNetwork) {
      contextData["videonetwork"] = this._entitlementData?.network || this._entitlementData?.seriesNetwork || this._entitlementData?.videoNetwork
    }
    if(this._entitlementData?.contentType === "LINEARCHANNEL") {
      contextData["videoprogram"] = this.lcProgrammeName || window?.lcProgrammeName || this._entitlementData?.gist?.title
      contextData["videotitle"] = this.lcSubTitle || window?.lcProgrammeName || this._entitlementData?.gist?.title
    }
    contextData[ADB.Media.VideoMetadataKeys.AssetId] = this._entitlementData?.gist?.id
    const AuthenticationStore = getStorageItem('AuthenticationStore')
    let userInfo = AuthenticationStore?.user
    if(userInfo?.tveMetadata?.mvpdProvider) {
      contextData["videomvpd"] = userInfo?.tveMetadata?.mvpdProvider
      contextData["videocallsign"] = userInfo?.tveMetadata?.mvpdProvider
      contextData["videopassguid"] = userInfo?.tveMetadata?.tveUserId
    }
    if(this._entitlementData?.videotmsid || window?.lcProgrammeDetails?.videotmsid) {
      contextData["videotmsid"] =  this.videotmsid || this._entitlementData.videotmsid || window.lcProgrammeDetails.videotmsid
    }

    if(this._entitlementData?.gist?.primaryCategory) {
      contextData["videosubcat1"] = this._entitlementData?.gist?.primaryCategory?.title
    }
    if(this._entitlementData?.categories?.length) {
      contextData["videosubcat2"] = this._entitlementData?.categories.map((cat) => cat.title).join(", ")
    }
    if(window?.epsNumber || window?.epsNumber === 0 || window.lcProgrammeDetails?.seriesDetails) {
      contextData["videoepnumber"] = window?.epsNumber || window.lcProgrammeDetails?.seriesDetails?.episode || "N/A"
    }
    if (window?.videoseason || window.lcProgrammeDetails?.seriesDetails) {
      contextData["videoseason"] = window.videoseason ||  window.lcProgrammeDetails?.seriesDetails?.season || "N/A"
    }
    return contextData
  }

  VideoAnalyticsProviderWrapper = VideoAnalyticsProvider
})();